import { Website } from '@betterfunnel/types';
import { DialogTitle } from '@headlessui/react';
import * as Sentry from '@sentry/react';
import { QueryDocumentSnapshot } from 'firebase/firestore';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import Modal, { ModalActionData } from '../components/Common/Modal';
import TextField from '../components/Common/TextField';
import Tooltip from '../components/Common/Tooltip';
import { DashboardContext } from '../contexts/DashboardContext';
import { DynamicPageContext } from '../contexts/DynamicPageContext';
import { UserContext } from '../contexts/UserContext';
import { updateVariant } from '../services/DynamicPageVariantsService';
import { classNames } from '../utils';
import { getWeightsInPercentage } from '../utils/variant-utils';

interface Props {
  variantId?: string;
  tests?: QueryDocumentSnapshot<Website>[];
  open: boolean;
  onClose: () => void;
}

interface WebsiteWeight {
  websiteId: string;
  weight: number;
}

export default function EditTrafficSplitModal({
  variantId,
  tests,
  open,
  onClose,
}: Props) {
  const { selectedAccount } = useContext(UserContext);
  const { selectedDynamicPage } = useContext(DashboardContext);
  const { variants } = useContext(DynamicPageContext);

  const [weights, setWeights] = useState<WebsiteWeight[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<Error>();

  const variant = useMemo(
    () => (variantId ? variants?.find((v) => v.id === variantId) : undefined),
    [variantId, variants],
  );

  useEffect(() => {
    if (!variant || !tests || !selectedAccount || !selectedDynamicPage) return;

    setWeights(getWeightsInPercentage(variant.data()));
  }, [variant, tests, selectedAccount, selectedDynamicPage]);

  useEffect(() => {
    if (open) return;

    setSaving(false);
    setError(undefined);
  }, [open]);

  const updateWeight = (websiteId: string, newWeight: number) => {
    setWeights((prevWeights) =>
      prevWeights.map((weight) =>
        weight.websiteId === websiteId
          ? { ...weight, weight: Math.max(0, Math.min(100, newWeight)) }
          : weight,
      ),
    );
  };

  const saveWeights = useCallback(async () => {
    if (saving || !variant || !selectedAccount || !selectedDynamicPage) return;

    setSaving(true);

    try {
      const websiteIdsWeights = weights.reduce(
        (acc, { websiteId, weight }) => ({
          ...acc,
          [websiteId]: weight,
        }),
        {},
      );

      await updateVariant(
        selectedAccount.id,
        selectedDynamicPage.id,
        variant.id,
        {
          websiteIdsWeights,
        },
      );

      onClose();
    } catch (e: any) {
      Sentry.captureException(e);
      console.error(e);
      setError(e);
    }
    setSaving(false);
  }, [saving, variant, selectedAccount, selectedDynamicPage, weights, onClose]);

  const totalWeight = useMemo(
    () => weights.reduce((sum, { weight }) => sum + weight, 0),
    [weights],
  );
  const isValidDistribution = useMemo(
    () => Math.abs(totalWeight - 100) < 0.01,
    [totalWeight],
  );

  const actions: ModalActionData[] = useMemo(
    () => [
      {
        title: 'Cancel',
        type: 'secondary',
        disabled: saving,
        onClick: () => onClose(),
      },
      {
        title: 'Update Weights',
        type: 'primary',
        loading: saving,
        disabled: !isValidDistribution,
        onClick: () => saveWeights(),
      },
    ],
    [saving, isValidDistribution, onClose, saveWeights],
  );

  const testsChars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

  return (
    <Modal open={open} onClose={onClose} actions={actions} width="lg">
      <DialogTitle
        as="h3"
        className="text-base font-semibold leading-6 text-gray-900"
      >
        Update Website Weights for Experiment
      </DialogTitle>
      <div className="mt-4 flex flex-col gap-4">
        {tests?.map((test, index) => {
          const { weight } = weights.find(
            (weight) => weight.websiteId === test.id,
          ) ?? { weight: 0 };
          return (
            <div key={test.id} className="flex items-center gap-4">
              <div className="flex-1">
                <Tooltip text={`Website: ${test.id}`} placement="right">
                  <span
                    className={classNames(
                      'inline-flex items-center rounded-md bg-blue-50 px-2 py-1.5 text-xs font-medium capitalize text-blue-700 ring-1 ring-inset ring-green-600/20',
                      index !== 0 ? 'bg-orange-50 text-orange-700' : '',
                    )}
                  >
                    {index === 0
                      ? 'Original'
                      : weights.length === 2
                        ? 'Experiment'
                        : `Experiment ${testsChars[index - 1] ?? testsChars[testsChars.length - 1]}`}
                  </span>
                </Tooltip>
              </div>
              <div className="w-32">
                <TextField
                  type="number"
                  value={weight.toString()}
                  onChange={(e) =>
                    updateWeight(test.id, Number(e.target.value))
                  }
                />
              </div>
            </div>
          );
        })}

        <div className="mt-2 flex items-center justify-between">
          <span className="text-sm text-gray-600">Total Distribution</span>
          <span
            className={`font-medium ${
              isValidDistribution ? 'text-green-600' : 'text-red-600'
            }`}
          >
            {totalWeight}%
          </span>
        </div>

        {!isValidDistribution && (
          <div className="text-sm text-red-600">
            Total distribution must equal 100%
          </div>
        )}
      </div>

      {error && (
        <div className="mt-5 w-full text-end text-red-600">
          {error.message || 'Failed to update weights'}
        </div>
      )}
    </Modal>
  );
}
